import React, { useRef, useState } from "react";
import { Link as RouterLink } from "react-router";
import { useSnackbar } from "notistack";
import Popover from "@mui/material/Popover";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import ButtonBase from "@mui/material/ButtonBase";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";

import { SWITCH_WORKSPACE_MUTATION } from "src/constants/graphql";
import WorkspaceLogo from "src/components/WorkspaceLogo";
import useAuth from "../../../../hooks/useAuth";
import useMutation from "src/hooks/useMutation";
import redirectToWorkspace from "src/utils/redirectToWorkspace";
import {
  ADMINISTRATE_PATH,
  isAdministrateWorkspace,
  sortWorkspaces,
  workspaceLabel,
  workspaceSecondary,
  WORKSPACE_TINT,
  WORKSPACE_TINT_HOVER,
} from "src/utils/workspaceDisplay";

// Row geometry, transition and hover follow the app's existing nav idiom
// (views/settings/SettingsNav.js, components/TableEntities/EntityTableRow.js).
const ROW_TRANSITION = "all 0.2s ease-in-out";

const styles = {
  accountButton: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    py: 0.5,
    px: 1,
    borderRadius: 2,
    transition: ROW_TRANSITION,
    "&:hover": {
      bgcolor: "grey.50",
    },
  },
  avatar: {
    width: 36,
    height: 36,
    bgcolor: "primary.main",
    color: "common.white",
    fontSize: 14,
    fontWeight: 500,
  },
  userInfoBox: {
    display: { xs: "none", md: "flex" },
    flexDirection: "column",
    alignItems: "flex-start",
  },
  userName: {
    fontWeight: 500,
    color: "text.primary",
    lineHeight: 1.3,
  },
  userEmail: {
    color: "text.secondary",
    lineHeight: 1.3,
  },
  // No radius or shadow override: MuiPaper already supplies the app's card
  // treatment (16px, soft two-layer shadow). Overriding it was what made this
  // panel look foreign.
  popover: {
    "& .MuiPopover-paper": {
      width: 320,
      mt: 1,
    },
  },
  identityHeader: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    px: 2,
    py: 2,
    bgcolor: "grey.50",
  },
  // Sentence case at weight 500 -- the app labels nothing in uppercase.
  sectionLabel: {
    display: "block",
    px: 2,
    pt: 1.5,
    pb: 0.75,
    color: "text.secondary",
    fontSize: 12,
    fontWeight: 500,
  },
  // Logos arrive at wildly different aspect ratios, so each is boxed in a fixed
  // tile. Without it the text column starts at a different x on every row.
  logoTile: {
    width: 44,
    height: 32,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 1,
    border: "1px solid",
    borderColor: "grey.200",
    bgcolor: "common.white",
    overflow: "hidden",
  },
  actionRow: {
    px: 2,
    py: 1.5,
    mx: 1.5,
    mb: 0.5,
    gap: 1.5,
    borderRadius: 2,
    transition: ROW_TRANSITION,
    "&:hover": {
      bgcolor: "grey.50",
    },
  },
  actionIcon: {
    fontSize: 20,
    color: "grey.600",
  },
};

const workspaceRowStyles = selected => ({
  px: 2,
  py: 1.5,
  mx: 1.5,
  mb: 0.5,
  gap: 1.5,
  borderRadius: 2,
  alignItems: "center",
  transition: ROW_TRANSITION,
  bgcolor: selected ? WORKSPACE_TINT : "transparent",
  "&:hover": {
    bgcolor: selected ? WORKSPACE_TINT_HOVER : "grey.50",
  },
});

const AccountMenu = () => {
  const ref = useRef(null);
  const { user, logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [isOpen, setOpen] = useState(false);
  const [switchWorkspace] = useMutation(SWITCH_WORKSPACE_MUTATION, { fetchPolicy: "no-cache" });
  const workspaces = sortWorkspaces(user?.workspaces || []);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = async () => {
    try {
      handleClose();
      await logout();
      window.location.replace("/login");
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Unable to logout", {
        variant: "error",
      });
    }
  };

  const handleSwitchWorkspace = workspace => {
    if (String(workspace.id) === String(user.company?.id)) {
      handleClose();
      if (isAdministrateWorkspace(workspace)) {
        window.location.assign(ADMINISTRATE_PATH);
      }
      return;
    }

    switchWorkspace({
      variables: { companyId: workspace.id },
      onSuccess: ({ user: targetUser }) => {
        redirectToWorkspace(targetUser);
      },
      onFailure: () => {
        enqueueSnackbar("Unable to switch workspace", { variant: "error" });
      },
    });
  };

  const getInitials = name => {
    if (!name) return "";
    const parts = name.split(" ");
    return parts
      .slice(0, 2)
      .map(part => part[0])
      .join("")
      .toUpperCase();
  };

  return (
    <>
      <ButtonBase id="homeIcon" ref={ref} onClick={handleOpen} sx={styles.accountButton}>
        <Avatar sx={styles.avatar}>{getInitials(user.name)}</Avatar>
        <Box sx={styles.userInfoBox}>
          <Typography variant="body2" sx={styles.userName}>
            {user.name || user.email}
          </Typography>
          <Typography variant="caption" sx={styles.userEmail}>
            {user.email}
          </Typography>
        </Box>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 20,
            color: "grey.600",
            display: { xs: "none", md: "block" },
            transform: isOpen ? "rotate(180deg)" : "none",
            transition: ROW_TRANSITION,
          }}
        />
      </ButtonBase>
      <Popover
        open={isOpen}
        anchorEl={ref.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={styles.popover}
      >
        <Box sx={styles.identityHeader}>
          <Avatar sx={styles.avatar}>{getInitials(user.name)}</Avatar>
          <Box minWidth={0}>
            <Typography fontWeight={500} color="textPrimary" noWrap>
              {user.name || user.email}
            </Typography>
            {user.name && (
              <Typography variant="caption" color="textSecondary" noWrap display="block">
                {user.email}
              </Typography>
            )}
          </Box>
        </Box>
        <Divider />
        {workspaces.length > 0 && (
          <>
            <Typography component="span" sx={styles.sectionLabel}>
              {workspaces.length > 1 ? "Switch workspace" : "Workspace"}
            </Typography>
            <List disablePadding>
              {workspaces.map(workspace => {
                const displayName = workspaceLabel(workspace);
                const selected = String(workspace.id) === String(user.company?.id);

                return (
                  <ListItemButton
                    key={workspace.id}
                    aria-label={displayName}
                    aria-current={selected ? "true" : undefined}
                    onClick={() => handleSwitchWorkspace(workspace)}
                    sx={workspaceRowStyles(selected)}
                  >
                    <Box sx={styles.logoTile}>
                      <WorkspaceLogo
                        name={displayName}
                        logoUrl={workspace.labelsLogoUrl}
                        width={36}
                        height={22}
                      />
                    </Box>
                    <ListItemText
                      primary={displayName}
                      secondary={workspaceSecondary(workspace)}
                      primaryTypographyProps={{
                        variant: "subtitle2",
                        fontWeight: 500,
                        color: "textPrimary",
                        noWrap: true,
                      }}
                      secondaryTypographyProps={{ variant: "caption", noWrap: true }}
                      sx={{ my: 0 }}
                    />
                    {selected && (
                      <CheckCircleRoundedIcon sx={{ fontSize: 20, color: "primary.main" }} />
                    )}
                  </ListItemButton>
                );
              })}
            </List>
            <Divider sx={{ mt: 1 }} />
          </>
        )}
        <List disablePadding sx={{ py: 1 }}>
          <ListItemButton
            id="homeIcon-profile"
            component={RouterLink}
            to="/profile"
            onClick={handleClose}
            sx={styles.actionRow}
          >
            <PersonOutlineIcon sx={styles.actionIcon} />
            <ListItemText
              primary="Profile"
              primaryTypographyProps={{ variant: "subtitle2", color: "textPrimary" }}
              sx={{ my: 0 }}
            />
          </ListItemButton>
          <ListItemButton id="homeIcon-logout" onClick={handleLogout} sx={styles.actionRow}>
            <LogoutOutlinedIcon sx={styles.actionIcon} />
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{ variant: "subtitle2", color: "textPrimary" }}
              sx={{ my: 0 }}
            />
          </ListItemButton>
        </List>
      </Popover>
    </>
  );
};

export default AccountMenu;
